import { sociometryLabels } from "../consts/const";
import { PupilResult } from "shared/types/sociometry";

export type SociometryRole = "stars" | "preferred" | "ignored" | "isolated" | "rejected";

export type SociometryRoleColors = Record<SociometryRole, string>;

export const getSociometryRoleColors = (type: SociometryRole) =>
    sociometryLabels.find((label) => label.label === type)?.color ?? "#98D8C8";

export const getSociometryTitle = (type: SociometryRole) =>
    sociometryLabels.find((label) => label.label === type)?.title ?? "";

export const getSociometryDescription = (type: SociometryRole) =>
    sociometryLabels.find((label) => label.label === type)?.description ?? "";

export const getSociometryStarsIds = (scores: PupilResult[]): Set<string> => {
    const maxPositive = scores.reduce((max, score) => Math.max(max, score.positiveScore || 0), 0);

    if (maxPositive <= 0) {
        return new Set();
    }

    const maxPositiveCandidates = scores.filter((score) => (score.positiveScore || 0) === maxPositive);
    const minNegative = maxPositiveCandidates.reduce(
        (min, score) => Math.min(min, score.negativeScore || 0),
        Number.POSITIVE_INFINITY,
    );

    const starsIds = new Set<string>();
    maxPositiveCandidates.forEach((score) => {
        const pupilId = score.pupil?._id;
        if (!pupilId) return;

        if ((score.negativeScore || 0) === minNegative) {
            starsIds.add(pupilId);
        }
    });

    return starsIds;
};

export const getSociometryRole = (score: PupilResult, starsIds: Set<string>): SociometryRole => {
    const pupilId = score.pupil?._id;
    const positiveScore = score.positiveScore || 0;
    const negativeScore = score.negativeScore || 0;

    if (positiveScore === 0 && negativeScore === 0) {
        return "isolated";
    }

    if (positiveScore === 0 && negativeScore > 0) {
        return "rejected";
    }

    if (pupilId && starsIds.has(pupilId)) {
        return "stars";
    }

    if (positiveScore > negativeScore) {
        return "preferred";
    }

    return "ignored";
};
